import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography,
} from '@mui/material';
import telegramUserService from '../../services/telegramUserService';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU');
};

function BotUsersContent({ botId }) {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState({ page: 1, limit: 25, search: '' });
  const [response, setResponse] = useState(null);
  const loading = response?.query !== query;
  const data = !loading ? response?.data : null;
  const error = !loading ? response?.error : null;

  useEffect(() => {
    let active = true;
    telegramUserService.getUsersByBot(botId, query).then(
      (result) => {
        if (!active) return;
        const lastPage = Math.max(1, Math.ceil(result.total / query.limit));
        if (query.page > lastPage) {
          setQuery({ ...query, page: lastPage });
          return;
        }
        setResponse({ query, data: result });
      },
      (failure) => {
        if (!active) return;
        const status = failure.response?.status;
        const message = status === 404 ? 'Бот не найден.'
          : status === 400 ? 'Некорректный идентификатор бота или параметры запроса.'
            : 'Не удалось загрузить пользователей бота. Повторите попытку.';
        setResponse({ query, error: message });
      },
    );
    return () => { active = false; };
  }, [botId, query]);

  const refresh = () => setQuery((current) => ({ ...current }));
  const applySearch = (event) => {
    event.preventDefault();
    setQuery((current) => ({ ...current, page: 1, search: searchInput.trim() }));
  };

  return (
    <Container maxWidth="xl">
      <Button component={Link} to="/telegram-bots" sx={{ mb: 2 }}>Назад к ботам</Button>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Typography variant="h4" component="h1">
          Пользователи бота{data?.bot?.username ? ` @${data.bot.username.replace(/^@/, '')}` : ''}
        </Typography>
        <Button onClick={refresh} disabled={loading}>Обновить</Button>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Последняя активность — глобальная активность пользователя во всех ботах, а не только в этом боте.
      </Typography>
      {data?.bot?.botType === 'private' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Здесь показаны исторические пользователи приватного бота, а не список разрешённого доступа.
        </Alert>
      )}
      <Box component="form" onSubmit={applySearch} display="flex" gap={2} mb={2}>
        <TextField
          label="Поиск по username, Telegram ID или имени"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          size="small"
          slotProps={{ htmlInput: { maxLength: 100 } }}
          fullWidth
        />
        <Button type="submit" variant="contained">Найти</Button>
      </Box>
      {loading && (
        <Box role="status" display="flex" alignItems="center" gap={2} py={3}>
          <CircularProgress size={24} /> Загрузка пользователей…
        </Box>
      )}
      {error && (
        <Alert severity="error" action={<Button color="inherit" onClick={refresh}>Повторить</Button>}>
          {error}
        </Alert>
      )}
      {data && (
        <>
          <Typography sx={{ mb: 2 }}>
            {query.search ? 'Найдено пользователей' : 'Всего пользователей'}: {data.total}
          </Typography>
          <Paper>
            <TableContainer>
              <Table aria-label="Пользователи бота">
                <TableHead>
                  <TableRow>
                    <TableCell>Telegram ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Имя и фамилия</TableCell>
                    <TableCell>Статус блокировки (глобальный)</TableCell>
                    <TableCell>Регистрация в системе</TableCell>
                    <TableCell>Последняя активность (во всех ботах)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.users.map((user) => (
                    <TableRow key={user._id || user.userId}>
                      <TableCell>{user.userId ?? '—'}</TableCell>
                      <TableCell>{user.username ? `@${user.username.replace(/^@/, '')}` : '—'}</TableCell>
                      <TableCell>{[user.firstName, user.lastName].filter(Boolean).join(' ').trim() || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={user.isBlocked ? 'Заблокирован' : 'Не заблокирован'} color={user.isBlocked ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell>{formatDate(user.joinDate)}</TableCell>
                      <TableCell>{formatDate(user.lastActivity)}</TableCell>
                    </TableRow>
                  ))}
                  {data.users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {query.search ? 'По вашему запросу пользователи не найдены.' : 'У этого бота пока нет пользователей.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data.total}
              page={query.page - 1}
              rowsPerPage={query.limit}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Строк на странице:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
              getItemAriaLabel={(type) => type === 'next' ? 'Следующая страница' : 'Предыдущая страница'}
              onPageChange={(_, page) => setQuery((current) => ({ ...current, page: page + 1 }))}
              onRowsPerPageChange={(event) => setQuery((current) => ({ ...current, page: 1, limit: Number(event.target.value) }))}
            />
          </Paper>
        </>
      )}
    </Container>
  );
}

export default function BotUsers() {
  const { id } = useParams();
  return <BotUsersContent key={id} botId={id} />;
}
